import React from "react";
import {
  Button,
  Flex,
  HStack,
  Link,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { gtagReportConversionThenNavigate } from "../../../utils/googleAdsPageViewConversion";
import { TiltCard } from "../../common/TiltCard";
import { usePublicGlassSurface } from "../../common/publicVisual";

/**
 * Shared home-page signup / login URLs for consistent routing.
 */
export const HOME_REGISTER_PATH = "/register";
export const HOME_LOGIN_PATH = "/login";

/**
 * Compact signup row used below major sections (e.g. Features).
 */
export function SectionSignupStrip({ heading = "Want to try it? Signing up is free." }) {
  const navigate = useNavigate();
  const glass = usePublicGlassSurface();
  const muted = useColorModeValue("gray.600", "gray.400");

  return (
    <TiltCard
      tilt={6}
      mt={{ base: 10, md: 14 }}
      borderRadius="2xl"
      borderWidth="1px"
      p={{ base: 5, md: 7 }}
      sx={{ transformStyle: "preserve-3d" }}
      {...glass}
    >
      <Flex
        direction={{ base: "column", md: "row" }}
        align="center"
        justify="space-between"
        gap={{ base: 4, md: 6 }}
      >
        <Text
          fontSize={{ base: "md", md: "lg" }}
          fontWeight="semibold"
          color={muted}
          textAlign={{ base: "center", md: "left" }}
        >
          {heading}
        </Text>
        <HStack spacing={3} flexWrap="wrap" justify={{ base: "center", md: "flex-end" }}>
          <Button
            colorScheme="teal"
            size="md"
            onClick={() => gtagReportConversionThenNavigate(navigate, HOME_REGISTER_PATH)}
          >
            Create account
          </Button>
          <Button
            variant="outline"
            colorScheme="teal"
            size="md"
            onClick={() => gtagReportConversionThenNavigate(navigate, HOME_LOGIN_PATH)}
          >
            Sign in
          </Button>
        </HStack>
      </Flex>
    </TiltCard>
  );
}

/** Small typography links under hero primary CTAs — not full-width buttons */
function AuxDot() {
  return (
    <Text as="span" fontSize="sm" color="whiteAlpha.400" px={{ base: 1, sm: 2 }} userSelect="none">
      ·
    </Text>
  );
}

export function HeroAuxiliaryLinks({ onExploreFeatures }) {
  const subtle = "whiteAlpha.800";
  return (
    <Flex
      mt={{ base: 6, md: 7 }}
      pt={{ base: 5, md: 6 }}
      borderTopWidth="1px"
      borderColor="whiteAlpha.200"
      gap={0}
      rowGap={2}
      wrap="wrap"
      justify={{ base: "center", lg: "flex-start" }}
      align="baseline"
      maxW={{ base: "100%", lg: "480px" }}
      w={{ base: "100%", lg: "auto" }}
    >
      <Link
        href="#features"
        onClick={(e) => {
          e.preventDefault();
          onExploreFeatures();
        }}
        fontSize="sm"
        fontWeight="medium"
        color={subtle}
        textDecoration="underline"
        textDecorationColor="whiteAlpha.300"
        textUnderlineOffset="3px"
        _hover={{ color: "teal.100", textDecorationColor: "teal.300" }}
      >
        See all tools
      </Link>
      <AuxDot />
      <Link
        as={RouterLink}
        to="/pricing"
        fontSize="sm"
        fontWeight="medium"
        color={subtle}
        _hover={{ color: "teal.100" }}
      >
        Pricing
      </Link>
      <AuxDot />
      <Link
        as={RouterLink}
        to="/contact-us"
        fontSize="sm"
        fontWeight="medium"
        color={subtle}
        _hover={{ color: "teal.100" }}
      >
        Contact
      </Link>
    </Flex>
  );
}
